import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Product, Promotion } from './cartSlice';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

interface ApiProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice: number | null;
  badge: string | null;
  isFeatured: boolean;
  updatedAt: number;
  gender: 'hombre' | 'mujer' | 'unisex';
  category: { id: string; name: string; slug: string } | null;
  images: { id: string; url: string; sortOrder: number; isPrimary: boolean }[];
  sizes: { id: string; size: string; stock: number }[];
  promotions?: Promotion[];
}

export interface ApiCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
}

const GENDER_MAP: Record<string, string> = { hombre: 'Hombre', mujer: 'Mujer', unisex: 'Unisex' };

function getActivePromo(promos?: Promotion[]): Promotion | null {
  if (!promos || promos.length === 0) return null;
  const now = Math.floor(Date.now() / 1000);
  return promos.find(p => {
    if (!p.isActive) return false;
    if (p.startsAt !== null && p.startsAt > now) return false;
    if (p.endsAt !== null && p.endsAt < now) return false;
    return true;
  }) ?? null;
}

function mapProduct(p: ApiProduct): Product {
  const sorted    = [...p.images].sort((a, b) => a.sortOrder - b.sortOrder);
  const primary   = sorted.find(i => i.isPrimary) ?? sorted[0];
  const secondary = sorted.find(i => i !== primary) ?? null;
  return {
    id:              p.id,
    name:            p.name,
    description:     p.description,
    characteristics: '',
    sizes:           p.sizes.map(s => s.size).join(', '),
    price:           p.price,
    originalPrice:   p.originalPrice,
    stock:           p.sizes.reduce((sum, s) => sum + s.stock, 0),
    image:           primary?.url ?? '',
    imageSecondary:  secondary?.url ?? null,
    images:          sorted.map(i => i.url),
    badge:           p.badge,
    isFeatured:      p.isFeatured,
    category:        p.category?.name ?? 'Sin categoría',
    gender:          GENDER_MAP[p.gender] ?? 'Unisex',
    promotion:       getActivePromo(p.promotions),
  };
}

export type ProductSort = 'default' | 'price_asc' | 'price_desc' | 'alpha';

export interface GetProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  categorySlug?: string;
  gender?: string;
  sort?: ProductSort;
}

export interface ProductsPage {
  items: Product[];
  total: number;
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: BASE }),
  endpoints: (build) => ({
    getProducts: build.query<ProductsPage, GetProductsParams>({
      query: (p = {}) => {
        const params = new URLSearchParams({
          page:  String(p.page  ?? 1),
          limit: String(p.limit ?? 100),
        });
        if (p.search?.trim())                    params.set('search',   p.search.trim());
        if (p.categorySlug)                      params.set('category', p.categorySlug);
        if (p.gender)                            params.set('gender',   p.gender);
        if (p.sort && p.sort !== 'default')      params.set('sort',     p.sort);
        return `/api/v1/products?${params}`;
      },
      transformResponse: (r: { data: { items: ApiProduct[]; total: number } }) => ({
        items: (r.data.items ?? []).map(mapProduct),
        total: r.data.total ?? 0,
      }),
      keepUnusedDataFor: 60,
    }),
    getCategories: build.query<ApiCategory[], void>({
      query: () => '/api/v1/categories',
      transformResponse: (r: { data: ApiCategory[] }) => r.data ?? [],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetProductsQuery, useGetCategoriesQuery } = apiSlice;
